import {
  CloseOutlined,
  PauseOutlined,
  PlayCircleOutlined,
} from "@ant-design/icons";
import { Image, Progress, Spin } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FaPlayCircle } from "react-icons/fa";
import { IoMdDownload } from "react-icons/io";
import videoPreviewDefault from "../../assets/video_preview_default.jpg";
import { formatTimeBySeconds } from "../../utils/formatTime";
import { standardizeVolume } from "../../utils/Utils";
import { useGlobalDownloader } from '../../context/global/DownloadProvider';
import { useImageLoader } from "../customHook/ImageLoader";
import { MediaVideoModal } from "../modal/MediaVideoModal";

const VideoPreview = ({ file, isSelf, isInPanel = false }) => {
  const [isOpenMediaModal, setIsOpenMediaModal] = useState(false);
  const {
    imageUrl,
    loading: isImageLoading,
    error: imageError,
  } = useImageLoader(file?.videoImgResourceId);

  const containerStyle = useMemo(() => {
    const originalWidth = file?.width || 16;
    const originalHeight = file?.height || 9;
    const targetHeight = 250;

    const aspectRatio = originalWidth / originalHeight;
    const targetWidth = targetHeight * aspectRatio;

    return {
      maxHeight: "250px",
      width: `${targetWidth}px`,
      maxWidth: "380px",
      position: "relative",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: "#f0f0f0",
      borderRadius: "8px",
      overflow: "hidden",
    };
  }, [file?.width, file?.height]);

  const containerPanelStyle = useMemo(() => {
    return {
      height: `40px`,
      width: `40px`,
      position: "relative",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: "#f0f0f0",
      borderRadius: "8px",
      overflow: "hidden",
      border: "1px solid #d9d9d9",
      margin: "0px 8px",
    };
  }, []);

  const {
    downloads,
    startDownload,
    cancelDownload,
    pauseDownload,
    resumeDownload,
    DOWNLOAD_STATUS,
  } = useGlobalDownloader();

  const [fileState, setFileState] = useState({});
  const fileNameToSave = file?.fileName || "Willo-file";

  const handleResumeDownload = (e) => {
    e.stopPropagation();
    resumeDownload(file?.resourceId, fileNameToSave);
  };

  const handlePauseDownload = (e) => {
    e.stopPropagation();
    pauseDownload(file?.resourceId);
  };

  const handleDownload = (e) => {
    e.stopPropagation();
    startDownload(file?.resourceId, fileNameToSave, 0);
  };

  const handleCancel = (e) => {
    e.stopPropagation();
    cancelDownload(file?.resourceId);
  };

  const renderDownloadButton = useCallback(() => {
    switch (fileState?.status) {
      case DOWNLOAD_STATUS.PAUSED:
        return (
          <div className="flex gap-2">
            <button onClick={handleResumeDownload}>
              <PlayCircleOutlined
                style={{ fontSize: "20px", color: "green" }}
              />
            </button>
            <button onClick={handleCancel}>
              <CloseOutlined style={{ fontSize: "20px", color: "red" }} />
            </button>
          </div>
        );

      case DOWNLOAD_STATUS.DOWNLOADING:
        return (
          <>
            <button onClick={handlePauseDownload}>
              <PauseOutlined style={{ fontSize: "20px" }} />
            </button>
            <button onClick={handleCancel}>
              <CloseOutlined style={{ fontSize: "20px" }} />
            </button>
          </>
        );

      case DOWNLOAD_STATUS.SUCCESS:
        return (
          <button onClick={handleDownload}>
            <IoMdDownload style={{ fontSize: "20px", color: "green" }} />
          </button>
        );

      case DOWNLOAD_STATUS.ERROR:
        return (
          <div className="flex gap-2">
            <button onClick={handleCancel}>
              <CloseOutlined style={{ fontSize: "20px", color: "red" }} />
            </button>
          </div>
        );

      default:
        return (
          <button onClick={handleDownload}>
            <IoMdDownload style={{ fontSize: "20px", color: "green" }} />
          </button>
        );
    }
  }, [fileState?.status]);

  const subOriginalName = () => {
    const maxLength = 15;
    let newOriginalName;

    if (file?.originalName?.trim()?.length > maxLength + 7) {
      newOriginalName =
        file?.originalName?.slice(0, maxLength) +
        "..." +
        file?.originalName?.slice(-7);
    } else {
      newOriginalName = file?.originalName;
    }

    return newOriginalName?.trim() || "Unknown.file";
  };

  const renderContent = useMemo(() => {
    const imageClassName = "cursor-pointer w-full h-full object-cover";

    if (isImageLoading) {
      return <Spin />;
    }

    if (imageError) {
      return (
        <Image
          src={videoPreviewDefault}
          preview={false}
          className={imageClassName}
        />
      );
    }

    if (imageUrl) {
      return (
        <Image
          height={isInPanel && "100%"}
          width={isInPanel && "100%"}
          src={imageUrl}
          preview={false}
          className={imageClassName}
        />
      );
    }

    return <div className="w-full h-full"></div>;
  }, [imageUrl, isImageLoading, imageError, isInPanel]);

  const progressRate = useMemo(() => {
    return (
      <div className="flex gap-[2px]">
        <div>
          {fileState?.progress
            ? standardizeVolume((fileState?.progress * file?.volume) / 100)
            : 0}
        </div>
        <div>/</div>
        <div>{standardizeVolume(file?.volume)}</div>
      </div>
    );
  }, [fileState?.progress]);

  useEffect(() => {
    setFileState(downloads[file?.resourceId]);
  }, [downloads]);

  return (
    <div className="flex flex-col max-w-full">
      <button
        className={`flex mb-[2px] w-full mt-[4px]`}
        style={{
          justifyContent: `${isSelf ? "flex-end" : "flex-start"}`,
          margin: !isInPanel && "auto",
        }}
        onClick={() => setIsOpenMediaModal(true)}
      >
        <div style={isInPanel ? containerPanelStyle : containerStyle}>
          <div
            className={`left-duration ${isInPanel ? "top-[10px] left-[0]" : ""}`}
          >
            {!isInPanel && formatTimeBySeconds(Math.round(file?.duration))}
          </div>

          {renderContent}

          <div className="center-play">
            <FaPlayCircle size={isInPanel ? 10 : 50} />
          </div>
        </div>
        {isInPanel && (
          <div className="text-[12px] flex flex-1 flex-col justify-center items-start ">
            <div>{subOriginalName()}</div>
            <div className="flex w-full gap-[10px] justify-between file-info">
              <div className="flex gap-[5px]">
                <span>{(file?.volume / (1024 * 1024)).toFixed(2)} MB </span>{" "}
                <span>-</span>
                <span>{formatTimeBySeconds(Math.round(file?.duration))}</span>
              </div>

              <span className="me-[38px]">{renderDownloadButton()}</span>
            </div>
          </div>
        )}
      </button>
      {[DOWNLOAD_STATUS.DOWNLOADING].includes(fileState?.status) &&
        isInPanel && (
          <div className="flex items-center gap-[2px]">
            <Progress
              className="flex-1"
              percent={fileState?.progress}
              showInfo={false}
              strokeColor="#52c41a"
              size="small"
            />
            <div className="text-[grey] text-[10px]">{progressRate}</div>
          </div>
        )}
      {isOpenMediaModal && (
        <MediaVideoModal
          open={isOpenMediaModal}
          onClose={() => setIsOpenMediaModal(false)}
          shortName={file?.originalName}
          file={file}
        />
      )}
    </div>
  );
};
export { VideoPreview };

